import { SunIcon, MoonIcon, SystemIcon } from "@/components/icons";
import { useColorScheme, type ColorSchemePreference } from "@/theme/colorScheme";
import { cn } from "@/lib/cn";

const OPTIONS: Array<{
  value: ColorSchemePreference;
  label: string;
  Icon: typeof SunIcon;
}> = [
  { value: "light", label: "Light", Icon: SunIcon },
  { value: "system", label: "System", Icon: SystemIcon },
  { value: "dark", label: "Dark", Icon: MoonIcon },
];

/**
 * All three viewer states are first-class: an explicit light or dark choice,
 * and the system default that follows `prefers-color-scheme`.
 */
export function ThemeToggle() {
  const preference = useColorScheme((state) => state.preference);
  const setPreference = useColorScheme((state) => state.setPreference);

  return (
    <div
      role="radiogroup"
      aria-label="Color theme"
      className="inline-flex items-center gap-0.5 rounded-full border border-border bg-card p-0.5"
    >
      {OPTIONS.map(({ value, label, Icon }) => {
        const active = preference === value;
        return (
          <button
            key={value}
            type="button"
            role="radio"
            aria-checked={active}
            title={label}
            onClick={() => setPreference(value)}
            className={cn(
              "inline-flex size-7 items-center justify-center rounded-full motion-fast",
              "transition-[background-color,color]",
              active
                ? "bg-accent-subtle text-accent"
                : "text-ink-secondary hover:text-ink hover:bg-hover",
            )}
          >
            <Icon className="size-4" />
            <span className="sr-only">{label}</span>
          </button>
        );
      })}
    </div>
  );
}
